import React, { useEffect, useState, useMemo } from 'react';
import { Alert, Button, Card, Col, Container, Form, Modal, Nav, Row, Spinner, Table, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';

type EmailType = 'weekly_picks' | 'weekly' | 'episode_results' | 'kickoff' | 'rules' | 'custom';
interface HistoryItem { id: string; season: number; type: string; subject: string; recipientCount: number; sentAt: string; status: string; recipients?: string[]; }
interface SavedPrompt { id: string; prompt: string; createdAt: string; lastUsedAt: string; }
interface EmailAsset { url: string; title: string; alt: string; }
interface RecipientCandidate { email: string; userName: string; leagueName: string; season: number; isCurrentSeason: boolean; suppressed: boolean; isAdditional?: boolean; }
interface SavedEmailDraft {
  id: string;
  name: string;
  season: number;
  type: EmailType;
  episode?: number;
  subject: string;
  body: string;
  assets: EmailAsset[];
  prompt?: string;
  extraEmails?: string;
  createdAt: string;
  updatedAt: string;
}

interface GeneratedEpisodeEmail {
  id: string;
  recipientEmail: string;
  recipientName: string;
  userName: string;
  leagueId: string;
  leagueName: string;
  season: number;
  startEpisode: number;
  endEpisode: number;
  episodeLabel: string;
  subject: string;
  html: string;
  episodeScore: number;
  seasonTotalScore: number;
  leagueRank: number;
  totalLeagueMembers: number;
}

const AdminEmail: React.FC = () => {
  const [season, setSeason] = useState(Number(localStorage.getItem('adminSelectedSeason') || 51));
  const [type, setType] = useState<EmailType>('weekly_picks');
  const [episode, setEpisode] = useState(2);
  const [endEpisode, setEndEpisode] = useState(2);
  const [isMultiEpisode, setIsMultiEpisode] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [extraEmails, setExtraEmails] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [assets, setAssets] = useState<EmailAsset[]>([]);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [savedPrompts, setSavedPrompts] = useState<SavedPrompt[]>([]);
  const [savedDrafts, setSavedDrafts] = useState<SavedEmailDraft[]>([]);
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  const [showSaveDraftModal, setShowSaveDraftModal] = useState(false);
  const [draftTitleInput, setDraftTitleInput] = useState('');
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [candidates, setCandidates] = useState<RecipientCandidate[]>([]);
  const [recipientSearch, setRecipientSearch] = useState('');
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [resendOpenId, setResendOpenId] = useState<string | null>(null);
  const [resendValue, setResendValue] = useState('');
  const [editorTab, setEditorTab] = useState<'edit' | 'preview'>('edit');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showImageSearchModal, setShowImageSearchModal] = useState(false);
  const [availableSeasonAssets, setAvailableSeasonAssets] = useState<EmailAsset[]>([]);
  const [imageSearchQuery, setImageSearchQuery] = useState('');
  const [isLoadingAssets, setIsLoadingAssets] = useState(false);
  const [customImageUrl, setCustomImageUrl] = useState('');
  const [customImageTitle, setCustomImageTitle] = useState('');
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // --- Personalized Episode Results State ---
  const [episodeEmails, setEpisodeEmails] = useState<GeneratedEpisodeEmail[]>([]);
  const [selectedEmailIndex, setSelectedEmailIndex] = useState<number>(0);
  const [isGeneratingEpisodeEmails, setIsGeneratingEpisodeEmails] = useState(false);
  const [isSendingBulkEpisodeEmails, setIsSendingBulkEpisodeEmails] = useState(false);
  const [bulkSendStatusText, setBulkSendStatusText] = useState<string>('');
  const [episodeLeagueFilter, setEpisodeLeagueFilter] = useState<string>('all');
  const [seasonLeagues, setSeasonLeagues] = useState<Array<{ id: string; name: string }>>([]);
  const api = process.env.REACT_APP_API_URL;

  const escapeEmailHtml = (value: string) => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const isHtmlContent = (text: string) => /<[a-z!][\s\S]*>/i.test(text);

  const normalizePublicUrls = (text: string): string => {
    if (!text) return '';
    return text.replace(/https?:\/\/(?:34\.148\.239\.22|localhost|127\.0\.0\.1)(?::\d+)?/gi, 'https://survivorrealityfantasypool.com');
  };

  const getPreviewImageUrl = (url: string) => {
    if (!url) return '';
    if (url.startsWith('https://static.wikia.nocookie.net/')) {
      return `${api}/api/admin/image-proxy?url=${encodeURIComponent(url)}`;
    }
    return url;
  };

  const transformPreviewHtml = (htmlContent: string) => {
    const normalized = normalizePublicUrls(htmlContent);
    return normalized.replace(/<img\b([^>]*)>/gi, (_, attrs) => {
      let updatedAttrs = attrs.replace(/\bsrc=["']([^"']+)["']/i, (_m: string, srcUrl: string) => {
        return `src="${getPreviewImageUrl(srcUrl)}"`;
      });
      if (!/referrerpolicy/i.test(updatedAttrs)) {
        updatedAttrs += ' referrerpolicy="no-referrer"';
      }
      return `<img ${updatedAttrs}>`;
    });
  };

  const renderSampleHtml = (textBody: string, imageAssets: EmailAsset[]) => {
    // Replace placeholders with realistic sample data
    let sample = normalizePublicUrls(textBody)
      .replace(/\{\{userName\}\}/g, 'Sandra Diaz-Twine')
      .replace(/\{\{leagueName\}\}/g, `Survivor Season ${season} Pool`)
      .replace(/\{\{season\}\}/g, String(season))
      .replace(/\{\{score\}\}/g, '142')
      .replace(/\{\{rank\}\}/g, '3rd')
      .replace(/\{\{weeklyPoints\}\}/g, '+24')
      .replace(/\{\{nextDeadline\}\}/g, 'Wednesday at 8:00 PM EST');

    const safeAssets = (Array.isArray(imageAssets) ? imageAssets : []).filter(a => /^https?:\/\//.test(a?.url || '')).slice(0, 6);
    const unusedAssets = safeAssets.filter(a => !sample.includes(a.url));
    const [hero, ...supporting] = unusedAssets;
    
    const heroHtml = hero ? `
      <div style="margin: 0 0 24px; text-align: center;">
        <img src="${getPreviewImageUrl(hero.url)}" alt="${escapeEmailHtml(String(hero.alt || hero.title || 'Survivor Fantasy'))}" referrerpolicy="no-referrer" style="display: block; width: 100%; max-width: 720px; height: auto; margin: 0 auto; border-radius: 8px;" />
        <div style="font-size: 12px; color: #68717a; margin-top: 6px;">${escapeEmailHtml(String(hero.title || ''))}</div>
      </div>` : '';
      
    const supportingHtml = supporting.length ? `
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="margin: 24px 0; border-collapse: collapse;">
        <tr>
          ${supporting.slice(0, 3).map(asset => `
            <td style="width: 33.33%; padding: 6px; text-align: center; vertical-align: top;">
              <img src="${getPreviewImageUrl(asset.url)}" alt="${escapeEmailHtml(String(asset.alt || asset.title || ''))}" referrerpolicy="no-referrer" style="display: block; width: 100%; max-width: 200px; height: 130px; object-fit: cover; margin: 0 auto; border-radius: 6px; border: 1px solid #e2e8f0;" />
              <div style="font-size: 11px; color: #68717a; margin-top: 5px; font-weight: 500;">${escapeEmailHtml(String(asset.title || ''))}</div>
            </td>
          `).join('')}
        </tr>
      </table>` : '';

    const isHtml = isHtmlContent(sample);
    if (isHtml && (sample.includes('<table') || sample.includes('<!DOCTYPE') || sample.includes('<html'))) {
      return transformPreviewHtml(sample);
    }
    const contentHtml = isHtml
      ? `<div>${transformPreviewHtml(sample)}</div>`
      : `<div style="white-space: pre-wrap;">${escapeEmailHtml(sample).replace(/\n/g, '<br/>')}</div>`;

    return `
      <div style="max-width: 720px; margin: 0 auto; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; font-size: 16px; line-height: 1.65; color: #202a33; background: #ffffff; padding: 20px; border: 1px solid #e2e8f0; border-radius: 8px;">
        ${heroHtml}
        ${contentHtml}
        ${supportingHtml}
        <div style="margin-top: 32px; padding-top: 16px; border-top: 1px solid #edf2f7; text-align: center; font-size: 12px; color: #a0aec0;">
          Survivor Fantasy League &bull; Season ${season}
        </div>
      </div>
    `;
  };

  const loadHistory = async () => {
    const response = await fetch(`${api}/api/admin/email-history`);
    if (response.ok) setHistory((await response.json()).history || []);
  };

  const loadSavedDrafts = async () => {
    try {
      const response = await fetch(`${api}/api/admin/saved-email-drafts`);
      if (response.ok) {
        const data = await response.json();
        setSavedDrafts(data.drafts || []);
      }
    } catch { /* ignore network error on draft listing */ }
  };

  const loadCandidates = async (targetSeason: number) => {
    try {
      const response = await fetch(`${api}/api/admin/email-recipients?season=${targetSeason}`);
      if (!response.ok) return;
      const data = await response.json();
      setCandidates(data.candidates || []);
    } catch { /* ignore, network errors just leave the list empty */ }
  };

  const loadSeasonLeagues = async (targetSeason: number) => {
    try {
      const response = await fetch(`${api}/api/leagues/all`);
      if (response.ok) {
        const data = await response.json();
        const filtered = (data.leagues || []).filter((l: any) => Number(l.season || 49) === Number(targetSeason));
        setSeasonLeagues(filtered.map((l: any) => ({ id: l.id, name: l.name })));
      }
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadHistory().catch(() => undefined);
    loadSavedDrafts().catch(() => undefined);
    fetch(`${api}/api/admin/email-prompts`).then(response => response.ok ? response.json() : { prompts: [] }).then(data => setSavedPrompts(data.prompts || [])).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadCandidates(season).catch(() => undefined);
    loadSeasonLeagues(season).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [season]);

  const handleGenerateEpisodeResultsEmails = async () => {
    setIsGeneratingEpisodeEmails(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(`${api}/api/admin/generate-episode-results-emails`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          season,
          episode,
          endEpisode: isMultiEpisode ? endEpisode : episode,
          leagueId: episodeLeagueFilter !== 'all' ? episodeLeagueFilter : undefined,
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to generate episode results emails');
      setEpisodeEmails(data.emails || []);
      setSelectedEmailIndex(0);
      const epText = isMultiEpisode && data.startEpisode !== data.endEpisode ? `Episodes ${data.startEpisode}–${data.endEpisode}` : `Episode ${data.startEpisode}`;
      setSuccess(`🎉 Generated ${data.count} personalized results email(s) for Season ${season}, ${epText}! Review them below before bulk sending.`);
    } catch (err: any) {
      setError(err.message || 'Failed to generate personalized episode results emails.');
    } finally {
      setIsGeneratingEpisodeEmails(false);
    }
  };

  const handleBulkSendEpisodeEmails = async () => {
    if (!episodeEmails.length) {
      setError('Please generate episode results emails first.');
      return;
    }

    const currentEpLabel = episodeEmails[0]?.episodeLabel || (isMultiEpisode ? `Episodes ${episode}–${endEpisode}` : `Episode ${episode}`);
    const confirmMsg = `Send ${episodeEmails.length} personalized ${currentEpLabel} results email(s)?\n\nEach recipient will receive their tailored team scorecard & Gemini analysis, and you will be CC'd on each one.`;
    if (!window.confirm(confirmMsg)) return;

    setIsSendingBulkEpisodeEmails(true);
    setBulkSendStatusText(`Starting send to ${episodeEmails.length} players...`);
    setError(null);
    setSuccess(null);

    const BATCH_SIZE = 10;
    let totalSent = 0;
    let totalFailed = 0;
    const errors: string[] = [];

    try {
      const totalBatches = Math.ceil(episodeEmails.length / BATCH_SIZE);

      for (let i = 0; i < episodeEmails.length; i += BATCH_SIZE) {
        const batch = episodeEmails.slice(i, i + BATCH_SIZE);
        const batchNum = Math.floor(i / BATCH_SIZE) + 1;
        const currentProcessed = Math.min(i + BATCH_SIZE, episodeEmails.length);

        setBulkSendStatusText(`Sending batch ${batchNum} of ${totalBatches} (${currentProcessed}/${episodeEmails.length})...`);

        const response = await fetch(`${api}/api/admin/bulk-send-episode-results`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ emails: batch })
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || `Failed on batch ${batchNum}`);
        }

        totalSent += (data.sentCount || 0);
        totalFailed += (data.failedCount || 0);

        if (Array.isArray(data.results)) {
          data.results.forEach((r: any) => {
            if (r.status === 'failed') {
              errors.push(`${r.userName} (${r.email}): ${r.error || 'Failed'}`);
            }
          });
        }
      }

      setSuccess(`🎉 Successfully sent ${totalSent} personalized results email(s)!${totalFailed > 0 ? ` (${totalFailed} failed/skipped)` : ''}`);
      if (errors.length > 0) {
        setError(`Notice: Some emails could not be sent: ${errors.slice(0, 5).join('; ')}`);
      }
      await loadHistory();
    } catch (err: any) {
      setError(err.message || 'Failed to bulk send episode results emails.');
    } finally {
      setIsSendingBulkEpisodeEmails(false);
      setBulkSendStatusText('');
    }
  };

  const handleSendSingleTestEpisodeEmail = async () => {
    const current = episodeEmails[selectedEmailIndex];
    if (!current) {
      setError('Select a generated email to test.');
      return;
    }
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const response = await fetch(`${api}/api/admin/email-test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject: current.subject,
          body: current.html,
          assets: []
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to send test email');
      setSuccess(data.message || 'Test email sent to admin!');
    } catch (err: any) {
      setError(err.message || 'Failed to send test email.');
    } finally {
      setIsLoading(false);
    }
  };

  // Re-derive the default selection whenever the candidate list or email type changes; admins can still adjust manually.
  useEffect(() => {
    const isSeasonMembers = type === 'weekly' || type === 'weekly_picks';
    const defaults = candidates.filter(candidate => !candidate.suppressed && (isSeasonMembers ? candidate.isCurrentSeason : !candidate.isCurrentSeason));
    setSelectedEmails(new Set(defaults.map(candidate => candidate.email.toLowerCase())));
  }, [candidates, type]);

  const generateDraft = async () => {
    setIsLoading(true); setError(null); setSuccess(null);
    try {
      const response = await fetch(`${api}/api/admin/email-draft`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          season,
          type,
          episode,
          prompt,
          currentSubject: subject,
          currentBody: body,
          currentAssets: assets,
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Could not generate email draft');
      setSubject(data.subject || '');
      setBody(data.body || '');
      if (Array.isArray(data.assets) && data.assets.length > 0) {
        setAssets(data.assets);
      }
      setSuccess('Draft updated. Preserved rich formatting and updated content.');
    } catch (err: any) { setError(err.message); } finally { setIsLoading(false); }
  };

  const toggleRecipient = (email: string) => {
    setSelectedEmails(prev => {
      const next = new Set(prev);
      const key = email.toLowerCase();
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const filteredCandidates = useMemo(() => {
    if (!recipientSearch.trim()) return candidates;
    const q = recipientSearch.toLowerCase().trim();
    return candidates.filter(candidate =>
      (candidate.userName && candidate.userName.toLowerCase().includes(q)) ||
      (candidate.email && candidate.email.toLowerCase().includes(q)) ||
      (candidate.leagueName && candidate.leagueName.toLowerCase().includes(q))
    );
  }, [candidates, recipientSearch]);

  const selectAllCandidates = (checked: boolean) => {
    if (recipientSearch.trim()) {
      setSelectedEmails(prev => {
        const next = new Set(prev);
        filteredCandidates.forEach(candidate => {
          if (!candidate.suppressed) {
            if (checked) {
              next.add(candidate.email.toLowerCase());
            } else {
              next.delete(candidate.email.toLowerCase());
            }
          }
        });
        return next;
      });
    } else {
      setSelectedEmails(checked ? new Set(candidates.filter(candidate => !candidate.suppressed).map(candidate => candidate.email.toLowerCase())) : new Set());
    }
  };

  const toggleSuppression = async (email: string, suppressed: boolean) => {
    try {
      if (suppressed) {
        await fetch(`${api}/api/admin/email-suppressions/${encodeURIComponent(email)}`, { method: 'DELETE' });
      } else {
        await fetch(`${api}/api/admin/email-suppressions`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) });
      }
      await loadCandidates(season);
    } catch (err: any) { setError(err.message || 'Could not update the email list'); }
  };

  const deleteAdditionalRecipient = async (email: string) => {
    try {
      await fetch(`${api}/api/admin/additional-recipients/${encodeURIComponent(email)}`, { method: 'DELETE' });
      await loadCandidates(season);
    } catch (err: any) {
      setError(err.message || 'Could not remove recipient');
    }
  };

  const sendCampaign = async () => {
    const extraList = extraEmails.split(/[\s,;]+/).map(email => email.trim()).filter(Boolean);
    const recipients = Array.from(new Set([...Array.from(selectedEmails), ...extraList.map(email => email.toLowerCase())]));
    if (!recipients.length) { setError('Select at least one recipient or add an extra email address.'); return; }
    const confirmation = type === 'weekly'
      ? `Send one shared email to ${recipients.length} Season ${season} recipient(s) on the To line?`
      : `Send this pre-game email by BCC to ${recipients.length} recipient(s)?`;
    if (!window.confirm(confirmation)) return;
    setIsLoading(true); setError(null); setSuccess(null);
    try {
      const response = await fetch(`${api}/api/admin/email-campaign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ season, type, subject, body, assets, recipients, extraEmails: extraList })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Could not send campaign');
      setSuccess(data.message);
      setExtraEmails('');
      await loadHistory();
      await loadCandidates(season);
    } catch (err: any) { setError(err.message); } finally { setIsLoading(false); }
  };

  const sendTestEmail = async () => {
    if (!subject.trim() || !body.trim()) { setError('Add a subject and message before sending a test.'); return; }
    setIsLoading(true); setError(null); setSuccess(null);
    try {
      const response = await fetch(`${api}/api/admin/email-test`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ subject, body, assets }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Could not send test email');
      setSuccess(data.message);
    } catch (err: any) { setError(err.message); } finally { setIsLoading(false); }
  };

  const handleSelectDraft = (draftId: string) => {
    if (!draftId) {
      setActiveDraftId(null);
      return;
    }
    const selected = savedDrafts.find(d => d.id === draftId);
    if (selected) {
      setSubject(selected.subject || '');
      setBody(selected.body || '');
      setAssets(Array.isArray(selected.assets) ? selected.assets : []);
      if (selected.season) setSeason(Number(selected.season));
      if (selected.type) setType(selected.type);
      if (selected.episode !== undefined) setEpisode(Number(selected.episode));
      if (selected.prompt !== undefined) setPrompt(selected.prompt);
      if (selected.extraEmails !== undefined) setExtraEmails(selected.extraEmails);
      setActiveDraftId(selected.id);
      setSuccess(`Loaded draft "${selected.name}"`);
    }
  };

  const handleStartNewDraft = () => {
    setActiveDraftId(null);
    setSubject('');
    setBody('');
    setAssets([]);
    setPrompt('');
    setExtraEmails('');
    setSuccess('Cleared editor. Ready for new email draft.');
  };

  const handleOpenSaveModal = (asNew = false) => {
    const active = activeDraftId ? savedDrafts.find(d => d.id === activeDraftId) : null;
    if (!asNew && active) {
      setDraftTitleInput(active.name);
    } else {
      setDraftTitleInput(subject.trim().slice(0, 45) || `Season ${season} ${type} Draft`);
    }
    setShowSaveDraftModal(true);
  };

  const handleConfirmSaveDraft = async (saveAsNew = false) => {
    if (!subject.trim() && !body.trim()) {
      setError('Cannot save an empty draft. Please provide a subject or message body.');
      return;
    }
    setIsSavingDraft(true);
    setError(null);
    try {
      const payload = {
        id: saveAsNew ? undefined : (activeDraftId || undefined),
        name: draftTitleInput.trim(),
        season,
        type,
        episode,
        subject,
        body,
        assets,
        prompt,
        extraEmails,
      };
      const response = await fetch(`${api}/api/admin/saved-email-drafts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to save draft');
      setSavedDrafts(data.drafts || []);
      if (data.draft) {
        setActiveDraftId(data.draft.id);
      }
      setShowSaveDraftModal(false);
      setSuccess(`Draft "${data.draft?.name || draftTitleInput}" saved successfully!`);
    } catch (err: any) {
      setError(err.message || 'Failed to save email draft');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleQuickSave = async () => {
    if (!activeDraftId) {
      handleOpenSaveModal(false);
      return;
    }
    const active = savedDrafts.find(d => d.id === activeDraftId);
    setDraftTitleInput(active?.name || subject.trim().slice(0, 45) || `Season ${season} ${type} Draft`);
    await handleConfirmSaveDraft(false);
  };

  const handleDeleteDraft = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const draft = savedDrafts.find(d => d.id === id);
    const draftName = draft?.name || 'this draft';
    if (!window.confirm(`Are you sure you want to delete "${draftName}"?`)) return;

    try {
      const response = await fetch(`${api}/api/admin/saved-email-drafts/${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to delete draft');
      setSavedDrafts(data.drafts || []);
      if (activeDraftId === id) {
        setActiveDraftId(null);
      }
      setSuccess(`Draft "${draftName}" deleted.`);
    } catch (err: any) {
      setError(err.message || 'Failed to delete draft');
    }
  };

  const updateAsset = (index: number, field: keyof EmailAsset, value: string) => {
    setAssets(current => current.map((asset, assetIndex) => assetIndex === index ? { ...asset, [field]: value } : asset));
  };

  const removeAsset = (index: number) => {
    setAssets(current => current.filter((_, assetIndex) => assetIndex !== index));
  };

  const addAsset = (newAsset: EmailAsset) => {
    if (!newAsset.url || !newAsset.url.trim()) return;
    if (assets.some(a => a.url === newAsset.url)) return;
    setAssets(current => [...current, newAsset]);
  };

  const loadSeasonAssets = async (targetSeason: number, query = '') => {
    setIsLoadingAssets(true);
    try {
      const qParam = query.trim() ? `&q=${encodeURIComponent(query.trim())}` : '';
      const response = await fetch(`${api}/api/admin/email-assets?season=${targetSeason}${qParam}`);
      if (!response.ok) throw new Error('Failed to load season images');
      const data = await response.json();
      setAvailableSeasonAssets(data.assets || []);
    } catch (err: any) {
      console.error('Error loading season assets:', err);
    } finally {
      setIsLoadingAssets(false);
    }
  };

  const handleOpenImageSearch = () => {
    setShowImageSearchModal(true);
    loadSeasonAssets(season, imageSearchQuery);
  };

  const handleAddCustomImage = () => {
    if (!customImageUrl.trim()) return;
    const title = customImageTitle.trim() || 'Custom Survivor Image';
    addAsset({ url: customImageUrl.trim(), title, alt: title });
    setCustomImageUrl('');
    setCustomImageTitle('');
  };

  const openResend = (item: HistoryItem) => {
    setResendOpenId(item.id);
    setResendValue((item.recipients || []).join(', '));
  };

  const sendResend = async () => {
    if (!resendOpenId) return;
    const targets = resendValue.split(/[\s,;]+/).map(email => email.trim()).filter(Boolean);
    if (!targets.length) { setError('Enter at least one email address to resend to.'); return; }
    setIsLoading(true); setError(null); setSuccess(null);
    try {
      const response = await fetch(`${api}/api/admin/email-resend`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ historyId: resendOpenId, recipients: targets }) });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Could not resend email');
      setSuccess(data.message); setResendOpenId(null); await loadHistory();
    } catch (err: any) { setError(err.message); } finally { setIsLoading(false); }
  };

  return (
    <Container className="mt-5 mb-5">
      <div className="mb-3"><Link to="/admin/dashboard">&larr; Back to Admin Home</Link></div>
      <Card className="shadow-sm home-card">
        <Card.Header as="h4" className="text-center">Admin Email Center</Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}
          <Row>
            <Col lg={4}><Card className="mb-3"><Card.Header>Campaign Setup</Card.Header><Card.Body>
              {/* Saved Drafts selector */}
              <div className="p-2 mb-3 bg-light rounded border">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <span className="small fw-bold text-dark">📁 Saved Drafts / Templates</span>
                  {activeDraftId && (
                    <Button variant="link" size="sm" className="p-0 text-decoration-none small" onClick={handleStartNewDraft}>
                      ➕ New Blank
                    </Button>
                  )}
                </div>
                <Form.Select 
                  size="sm" 
                  value={activeDraftId || ''} 
                  onChange={e => handleSelectDraft(e.target.value)}
                  className="mb-2"
                >
                  <option value="">{savedDrafts.length === 0 ? 'No saved drafts yet' : 'Select a saved draft to load...'}</option>
                  {savedDrafts.map(d => (
                    <option key={d.id} value={d.id}>
                      {d.name} (S{d.season} {d.type} - {new Date(d.updatedAt).toLocaleDateString()})
                    </option>
                  ))}
                </Form.Select>
                {activeDraftId && (
                  <div className="d-flex gap-1 justify-content-between">
                    <Button size="sm" variant="outline-success" className="w-50 py-1" onClick={handleQuickSave} disabled={isSavingDraft}>
                      💾 Update
                    </Button>
                    <Button size="sm" variant="outline-danger" className="w-50 py-1" onClick={e => handleDeleteDraft(activeDraftId, e)}>
                      🗑️ Delete
                    </Button>
                  </div>
                )}
              </div>

              <Form.Group className="mb-3"><Form.Label>Season</Form.Label><Form.Control type="number" value={season} onChange={e => setSeason(Number(e.target.value))} /></Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email Type</Form.Label>
                <Form.Select value={type} onChange={e => setType(e.target.value as EmailType)}>
                  <option value="episode_results">🎯 Episode Results (Personalized Scores + Gemini Analysis)</option>
                  <option value="weekly_picks">Weekly set your draft & elimination pick</option>
                  <option value="weekly">Weekly scoring update (Shared)</option>
                  <option value="kickoff">Game kickoff</option>
                  <option value="rules">Rules and instructions</option>
                  <option value="custom">Custom Gemini email</option>
                </Form.Select>
              </Form.Group>
              {type === 'episode_results' && (
                <>
                  <Form.Group className="mb-2">
                    <Form.Check 
                      type="checkbox"
                      id="multi-episode-checkbox"
                      label="Combine multiple episodes in this update"
                      checked={isMultiEpisode}
                      onChange={e => {
                        setIsMultiEpisode(e.target.checked);
                        if (!e.target.checked) setEndEpisode(episode);
                      }}
                      className="fw-bold text-primary"
                    />
                  </Form.Group>

                  {!isMultiEpisode ? (
                    <Form.Group className="mb-3">
                      <Form.Label>Episode</Form.Label>
                      <Form.Control type="number" min="1" value={episode} onChange={e => {
                        const val = Number(e.target.value);
                        setEpisode(val);
                        setEndEpisode(val);
                      }} />
                    </Form.Group>
                  ) : (
                    <Row className="mb-3 g-2">
                      <Col xs={6}>
                        <Form.Label className="small fw-bold">From Episode</Form.Label>
                        <Form.Control 
                          type="number" 
                          min="1" 
                          value={episode} 
                          onChange={e => setEpisode(Number(e.target.value))} 
                        />
                      </Col>
                      <Col xs={6}>
                        <Form.Label className="small fw-bold">Through Episode</Form.Label>
                        <Form.Control 
                          type="number" 
                          min={episode} 
                          value={endEpisode} 
                          onChange={e => setEndEpisode(Number(e.target.value))} 
                        />
                      </Col>
                    </Row>
                  )}

                  <Form.Group className="mb-3">
                    <Form.Label>League Filter (Optional)</Form.Label>
                    <Form.Select value={episodeLeagueFilter} onChange={e => setEpisodeLeagueFilter(e.target.value)}>
                      <option value="all">All Leagues in Season {season}</option>
                      {seasonLeagues.map(l => (
                        <option key={l.id} value={l.id}>{l.name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </>
              )}
              {(type === 'weekly' || type === 'weekly_picks') && (
                <Form.Group className="mb-3">
                  <Form.Label>Episode</Form.Label>
                  <Form.Control type="number" min="1" value={episode} onChange={e => setEpisode(Number(e.target.value))} />
                </Form.Group>
              )}
              <Alert variant="light" className="small">
                {type === 'episode_results'
                  ? (isMultiEpisode
                      ? `Combines Episodes ${Math.min(episode, endEpisode)} through ${Math.max(episode, endEpisode)} into a single comprehensive update for each player entry in Season ${season}, including individual episode breakdowns and Gemini player analysis.`
                      : `Generates custom personalized emails for every team entry in Season ${season}, Episode ${episode}. Includes active lineups, scoring breakdowns, and Gemini analysis per player.`)
                  : type === 'weekly_picks'
                  ? `Default recipients: Season ${season} members. Sends an urgent reminder to lock in rosters / active lineups and submit weekly elimination picks before Episode ${episode}.`
                  : type === 'weekly'
                  ? `Default recipients: Season ${season} members. Weekly updates unlock after drafts are locked and game scoring has started.`
                  : `Default recipients: members from seasons before ${season}. Adjust the list below or add extra addresses.`}
              </Alert>
              {type === 'episode_results' ? (
                <Button 
                  variant="primary" 
                  className="w-100 fw-bold py-2" 
                  onClick={handleGenerateEpisodeResultsEmails} 
                  disabled={isGeneratingEpisodeEmails}
                >
                  {isGeneratingEpisodeEmails ? (
                    <>
                      <Spinner size="sm" animation="border" className="me-2" />
                      Generating Results Emails...
                    </>
                  ) : (
                    isMultiEpisode && episode !== endEpisode
                      ? `✨ Generate Ep ${Math.min(episode, endEpisode)}–${Math.max(episode, endEpisode)} Results Emails`
                      : `✨ Generate Episode ${episode} Results Emails`
                  )}
                </Button>
              ) : (
                <>
                  <Form.Group className="mb-3"><Form.Label>Additional email addresses</Form.Label><Form.Control as="textarea" rows={2} value={extraEmails} onChange={e => setExtraEmails(e.target.value)} placeholder="guest@example.com, sponsor@example.com" /><Form.Text>Always included in addition to the recipient list below.</Form.Text></Form.Group>
                  <Form.Group className="mb-3"><Form.Label>Gemini prompt or iteration</Form.Label><Form.Control as="textarea" rows={5} value={prompt} onChange={e => setPrompt(e.target.value)} placeholder="Make the update shorter and more playful." /><Form.Text>Prompts are saved automatically when used.</Form.Text></Form.Group>
                  {savedPrompts.length > 0 && <Form.Group className="mb-3"><Form.Label>Reuse a saved prompt</Form.Label><Form.Select value="" onChange={e => { const saved = savedPrompts.find(item => item.id === e.target.value); if (saved) setPrompt(saved.prompt); }}><option value="">Select a saved prompt...</option>{savedPrompts.map(item => <option key={item.id} value={item.id}>{item.prompt.slice(0, 100)}</option>)}</Form.Select></Form.Group>}
                  <Button variant="outline-primary" className="w-100" onClick={generateDraft} disabled={isLoading}>{isLoading ? <Spinner size="sm" /> : 'Generate / Refine Draft'}</Button>
                </>
              )}
            </Card.Body></Card></Col>
            <Col lg={8}>
              {type === 'episode_results' ? (
                /* --- Personalized Episode Results View --- */
                <Card className="mb-3 border-primary shadow-sm">
                  <Card.Header className="bg-primary text-white d-flex justify-content-between align-items-center">
                    <span className="fw-bold fs-6">
                      🎯 {episodeEmails[0]?.episodeLabel ? `${episodeEmails[0].episodeLabel} Results Review` : (isMultiEpisode ? `Episodes ${episode}–${endEpisode} Results Review` : `Episode ${episode} Results Review`)} ({episodeEmails.length} Email{episodeEmails.length === 1 ? '' : 's'})
                    </span>
                    {episodeEmails.length > 0 && (
                      <Badge bg="light" text="dark" className="fs-6">
                        Entry {selectedEmailIndex + 1} of {episodeEmails.length}
                      </Badge>
                    )}
                  </Card.Header>
                  <Card.Body>
                    {episodeEmails.length === 0 ? (
                      <div className="text-center py-5">
                        <div className="fs-1 mb-2">📬</div>
                        <h5>No Episode Results Emails Generated Yet</h5>
                        <p className="text-muted" style={{ maxWidth: 500, margin: '0 auto 1.5rem' }}>
                          Select Season {season} and {isMultiEpisode ? `Episodes ${episode}–${endEpisode}` : `Episode ${episode}`} on the left, then click <strong>Generate Results Emails</strong> to build custom scorecards and Gemini analyses for all player entries.
                        </p>
                        <Button 
                          variant="primary" 
                          size="lg" 
                          onClick={handleGenerateEpisodeResultsEmails} 
                          disabled={isGeneratingEpisodeEmails}
                        >
                          {isGeneratingEpisodeEmails ? (
                            <>
                              <Spinner size="sm" animation="border" className="me-2" />
                              Generating Results Emails...
                            </>
                          ) : (
                            isMultiEpisode && episode !== endEpisode
                              ? `✨ Generate Season ${season} Ep ${episode}–${endEpisode} Results`
                              : `✨ Generate Season ${season} Episode ${episode} Results`
                          )}
                        </Button>
                      </div>
                    ) : (
                      <>
                        {/* Selector across all generated emails */}
                        <div className="p-3 bg-light rounded border mb-3">
                          <Row className="align-items-center g-2">
                            <Col md={7}>
                              <Form.Label className="fw-bold mb-1 small text-dark">
                                👤 Select Player Entry to Preview:
                              </Form.Label>
                              <Form.Select 
                                value={selectedEmailIndex} 
                                onChange={e => setSelectedEmailIndex(Number(e.target.value))}
                                className="fw-bold text-primary"
                              >
                                {episodeEmails.map((item, idx) => (
                                  <option key={item.id} value={idx}>
                                    #{idx + 1}: {item.userName} ({item.leagueName}) &rarr; {item.recipientEmail} [{item.episodeScore >= 0 ? `+${item.episodeScore}` : item.episodeScore} pts, Rank #{item.leagueRank}]
                                  </option>
                                ))}
                              </Form.Select>
                            </Col>
                            <Col md={5} className="d-flex justify-content-md-end gap-1">
                              <Button 
                                variant="outline-secondary" 
                                size="sm"
                                disabled={selectedEmailIndex <= 0}
                                onClick={() => setSelectedEmailIndex(prev => Math.max(0, prev - 1))}
                              >
                                &larr; Prev
                              </Button>
                              <Button 
                                variant="outline-secondary" 
                                size="sm"
                                disabled={selectedEmailIndex >= episodeEmails.length - 1}
                                onClick={() => setSelectedEmailIndex(prev => Math.min(episodeEmails.length - 1, prev + 1))}
                              >
                                Next &rarr;
                              </Button>
                              <Button 
                                variant="outline-info" 
                                size="sm"
                                onClick={handleGenerateEpisodeResultsEmails}
                                disabled={isGeneratingEpisodeEmails}
                              >
                                🔄 Regenerate
                              </Button>
                            </Col>
                          </Row>
                        </div>

                        {/* Current selected email summary metadata banner */}
                        {episodeEmails[selectedEmailIndex] && (
                          <div className="mb-3 p-3 bg-white rounded border">
                            <Row className="align-items-center">
                              <Col md={8}>
                                <div className="small text-muted mb-1">
                                  <strong>To:</strong> {episodeEmails[selectedEmailIndex].recipientName} &lt;{episodeEmails[selectedEmailIndex].recipientEmail}&gt;
                                </div>
                                <div className="small text-muted mb-1">
                                  <strong>CC:</strong> Sending Address (Admin)
                                </div>
                                <div className="small">
                                  <strong>Subject:</strong> {episodeEmails[selectedEmailIndex].subject}
                                </div>
                              </Col>
                              <Col md={4} className="text-md-end mt-2 mt-md-0">
                                <Badge bg="success" className="me-1 p-2">
                                  {episodeEmails[selectedEmailIndex].episodeLabel}: {episodeEmails[selectedEmailIndex].episodeScore >= 0 ? `+${episodeEmails[selectedEmailIndex].episodeScore}` : episodeEmails[selectedEmailIndex].episodeScore} pts
                                </Badge>
                                <Badge bg="info" text="dark" className="p-2">
                                  Rank #{episodeEmails[selectedEmailIndex].leagueRank}
                                </Badge>
                              </Col>
                            </Row>
                          </div>
                        )}

                        {/* Rendered HTML Preview */}
                        <div className="p-3 bg-light rounded border mb-3" style={{ maxHeight: 600, overflowY: 'auto' }}>
                          {episodeEmails[selectedEmailIndex] && (
                            <div 
                              className="email-html-preview-container"
                              dangerouslySetInnerHTML={{ __html: episodeEmails[selectedEmailIndex].html }} 
                            />
                          )}
                        </div>

                        {/* Action buttons: Test Single and Bulk Send All */}
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 pt-2 border-top">
                          <Button 
                            variant="outline-secondary" 
                            onClick={handleSendSingleTestEpisodeEmail} 
                            disabled={isLoading || isSendingBulkEpisodeEmails}
                          >
                            ✉️ Send Preview Email to Admin Only
                          </Button>
                          <Button 
                            variant="success" 
                            size="lg" 
                            className="fw-bold px-4"
                            onClick={handleBulkSendEpisodeEmails} 
                            disabled={isSendingBulkEpisodeEmails || episodeEmails.length === 0}
                          >
                            {isSendingBulkEpisodeEmails ? (
                              <>
                                <Spinner size="sm" animation="border" className="me-2" />
                                {bulkSendStatusText || `Sending to ${episodeEmails.length} Players...`}
                              </>
                            ) : (
                              `🚀 Bulk Send All ${episodeEmails.length} Episode Results (CC Admin)`
                            )}
                          </Button>
                        </div>
                      </>
                    )}
                  </Card.Body>
                </Card>
              ) : (
                /* --- Standard / Campaign Email View --- */
                <>
                  <Card className="mb-3">
                    <Card.Header className="d-flex justify-content-between align-items-center">
                      <span>Review Before Sending</span>
                      <div className="d-flex gap-2 align-items-center">
                        <Nav variant="pills" activeKey={editorTab} onSelect={k => setEditorTab((k as 'edit' | 'preview') || 'edit')}>
                          <Nav.Item>
                            <Nav.Link eventKey="edit" className="py-1 px-3" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                              ✏️ Edit
                            </Nav.Link>
                          </Nav.Item>
                          <Nav.Item>
                            <Nav.Link eventKey="preview" className="py-1 px-3" style={{ cursor: 'pointer', fontSize: '0.85rem' }}>
                              👁️ Inline Preview
                            </Nav.Link>
                          </Nav.Item>
                        </Nav>
                        <Button 
                          variant="outline-success" 
                          size="sm" 
                          onClick={() => handleOpenSaveModal(false)}
                          disabled={!subject.trim() && !body.trim()}
                          className="fw-bold"
                        >
                          💾 Save Draft
                        </Button>
                        <Button 
                          variant="outline-info" 
                          size="sm" 
                          onClick={() => setShowPreviewModal(true)}
                          disabled={!subject.trim() && !body.trim()}
                          className="fw-bold"
                        >
                          🔍 Fullscreen Preview
                        </Button>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      {activeDraftId && (
                        <Alert variant="info" className="d-flex justify-content-between align-items-center py-2 px-3 mb-3 small">
                          <div>
                            <strong>📌 Editing Saved Draft:</strong> {savedDrafts.find(d => d.id === activeDraftId)?.name} 
                            <span className="text-muted ms-2">
                              (Last saved: {savedDrafts.find(d => d.id === activeDraftId)?.updatedAt ? new Date(savedDrafts.find(d => d.id === activeDraftId)!.updatedAt).toLocaleTimeString() : ''})
                            </span>
                          </div>
                          <div className="d-flex gap-2">
                            <Button size="sm" variant="success" className="py-0 px-2" onClick={handleQuickSave} disabled={isSavingDraft}>
                              {isSavingDraft ? <Spinner size="sm" /> : '💾 Quick Update'}
                            </Button>
                            <Button size="sm" variant="outline-primary" className="py-0 px-2" onClick={() => handleOpenSaveModal(true)}>
                              Save as Copy
                            </Button>
                            <Button size="sm" variant="outline-secondary" className="py-0 px-2" onClick={() => setActiveDraftId(null)}>
                              ✕ Unlink
                            </Button>
                          </div>
                        </Alert>
                      )}

                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold">Subject</Form.Label>
                        <Form.Control value={subject} onChange={e => setSubject(e.target.value)} placeholder="Generate a draft or enter a subject" />
                      </Form.Group>

                      {editorTab === 'edit' ? (
                        <>
                          <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Message Body</Form.Label>
                            <Form.Control as="textarea" rows={14} value={body} onChange={e => setBody(e.target.value)} placeholder="Your reviewed email content..." />
                          </Form.Group>
                          
                          <Card className="mb-3 bg-light">
                            <Card.Header className="d-flex justify-content-between align-items-center">
                              <span className="fw-bold">🖼️ Web Assets Included in Email ({assets.length})</span>
                              <Button 
                                variant="primary" 
                                size="sm" 
                                onClick={handleOpenImageSearch}
                              >
                                🔎 Browse & Add Images
                              </Button>
                            </Card.Header>
                            <Card.Body>
                              {assets.length === 0 ? (
                                <div className="text-center py-3 text-muted">
                                  <p className="mb-2">No image assets attached to this email.</p>
                                  <Button variant="outline-primary" size="sm" onClick={handleOpenImageSearch}>
                                    Find Season {season} Images & Logos
                                  </Button>
                                </div>
                              ) : (
                                <Row>
                                  {assets.map((asset, index) => (
                                    <Col md={6} key={`${asset.url}-${index}`} className="mb-3">
                                      <Card className="h-100 shadow-sm">
                                        <Card.Img variant="top" src={getPreviewImageUrl(asset.url)} alt={asset.alt} referrerPolicy="no-referrer" style={{ maxHeight: 130, objectFit: 'contain', background: '#222' }} />
                                        <Card.Body className="p-2">
                                          <Form.Control className="mb-2" size="sm" value={asset.url} onChange={e => updateAsset(index, 'url', e.target.value)} placeholder="Image URL" />
                                          <Form.Control className="mb-2" size="sm" value={asset.title} onChange={e => updateAsset(index, 'title', e.target.value)} placeholder="Asset title" />
                                          <Form.Control className="mb-2" size="sm" value={asset.alt} onChange={e => updateAsset(index, 'alt', e.target.value)} placeholder="Alt text" />
                                          <Button size="sm" variant="outline-danger" className="w-100" onClick={() => removeAsset(index)}>Remove Asset</Button>
                                        </Card.Body>
                                      </Card>
                                    </Col>
                                  ))}
                                </Row>
                              )}
                            </Card.Body>
                          </Card>
                          <Form.Text className="text-muted">Placeholders: {'{{userName}}'}, {'{{leagueName}}'}, {'{{season}}'}.</Form.Text>
                        </>
                      ) : (
                        <div className="bg-light p-3 rounded border">
                          <div className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                            <span className="small text-muted fw-bold">Live Email Simulation</span>
                            <span className="badge bg-secondary">HTML Rendering</span>
                          </div>
                          <div className="mb-3 p-2 bg-white rounded border small">
                            <div><strong>From:</strong> Survivor Fantasy Pool &lt;commissioner@survivorrealityfantasypool.com&gt;</div>
                            <div><strong>To:</strong> Sandra Diaz-Twine &lt;sandra@example.com&gt;</div>
                            <div><strong>Subject:</strong> {subject || '(No subject entered)'}</div>
                          </div>
                          <div 
                            className="email-html-preview-container"
                            dangerouslySetInnerHTML={{ __html: renderSampleHtml(body || '*(No email message body entered yet)*', assets) }} 
                          />
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                  <Card className="mb-3">
                    <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <span className="fw-bold">Recipients ({selectedEmails.size} selected)</span>
                        {recipientSearch.trim() && (
                          <Badge bg="secondary">{filteredCandidates.length} matching</Badge>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2 flex-grow-1 justify-content-end" style={{ maxWidth: '480px' }}>
                        <div className="position-relative flex-grow-1" style={{ minWidth: '180px', maxWidth: '280px' }}>
                          <Form.Control
                            size="sm"
                            type="text"
                            placeholder="🔍 Search name or email..."
                            value={recipientSearch}
                            onChange={(e) => setRecipientSearch(e.target.value)}
                            className="pe-4"
                          />
                          {recipientSearch && (
                            <button
                              type="button"
                              className="btn btn-sm btn-link position-absolute end-0 top-50 translate-middle-y text-muted text-decoration-none p-1 me-1"
                              onClick={() => setRecipientSearch('')}
                              style={{ fontSize: '0.8rem', lineHeight: 1 }}
                            >
                              ✕
                            </button>
                          )}
                        </div>
                        <Button size="sm" variant="outline-secondary" onClick={() => selectAllCandidates(true)}>
                          {recipientSearch.trim() ? 'Select Filtered' : 'Select All'}
                        </Button>
                        <Button size="sm" variant="outline-secondary" onClick={() => selectAllCandidates(false)}>
                          {recipientSearch.trim() ? 'Clear Filtered' : 'Clear All'}
                        </Button>
                      </div>
                    </Card.Header>
                    <Card.Body style={{ maxHeight: 260, overflowY: 'auto' }}>
                      {filteredCandidates.length === 0 ? (
                        <p className="text-muted mb-0">
                          {recipientSearch.trim() ? `No recipients found matching "${recipientSearch}".` : 'No registered members found yet.'}
                        </p>
                      ) : (
                        <Table size="sm" responsive hover>
                          <thead><tr><th></th><th>Name</th><th>League</th><th>Season</th><th>Status</th></tr></thead>
                          <tbody>
                            {filteredCandidates.map(candidate => (
                              <tr key={candidate.email} className={candidate.suppressed ? 'text-muted' : undefined}>
                                <td><Form.Check checked={selectedEmails.has(candidate.email.toLowerCase())} disabled={candidate.suppressed} onChange={() => toggleRecipient(candidate.email)} /></td>
                                <td>
                                  {candidate.userName || candidate.email}
                                  {candidate.isAdditional && <span className="badge bg-info text-dark ms-2">Direct Add</span>}
                                  <div className="small text-muted">{candidate.email}</div>
                                </td>
                                <td>{candidate.leagueName}</td>
                                <td>{candidate.season}</td>
                                <td>
                                  <div className="d-flex gap-1">
                                    {candidate.suppressed
                                      ? <Button size="sm" variant="outline-success" onClick={() => toggleSuppression(candidate.email, true)}>Resubscribe</Button>
                                      : <Button size="sm" variant="outline-danger" onClick={() => toggleSuppression(candidate.email, false)}>Remove from list</Button>}
                                    {candidate.isAdditional && (
                                      <Button size="sm" variant="outline-secondary" title="Delete address completely" onClick={() => deleteAdditionalRecipient(candidate.email)}>
                                        🗑️
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </Card.Body>
                  </Card>
                  <div className="d-flex justify-content-end gap-2"><Button variant="outline-secondary" onClick={sendTestEmail} disabled={isLoading || !subject.trim() || !body.trim()}>Send Test Email</Button><Button variant="success" onClick={sendCampaign} disabled={isLoading || !subject.trim() || !body.trim()}>{type === 'weekly' ? `Send Shared Email to Season ${season}` : 'Send BCC to Selected Recipients'}</Button></div>
                </>
              )}
            </Col>
          </Row>
          <Card className="mt-4"><Card.Header>Send History</Card.Header><Card.Body>
            {history.length === 0 ? <p className="text-muted mb-0">No campaigns sent yet.</p> : (
              <Table responsive striped size="sm">
                <thead><tr><th>Date</th><th>Type</th><th>Season</th><th>Subject</th><th>Recipients</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {history.map(item => (
                    <React.Fragment key={item.id}>
                      <tr>
                        <td>{new Date(item.sentAt).toLocaleString()}</td>
                        <td>{item.type}</td>
                        <td>{item.season}</td>
                        <td>{item.subject}</td>
                        <td>{item.recipientCount}</td>
                        <td>{item.status}</td>
                        <td><Button size="sm" variant="outline-primary" onClick={() => openResend(item)}>Resend</Button></td>
                      </tr>
                      {resendOpenId === item.id && (
                        <tr>
                          <td colSpan={7}>
                            <Form.Group className="mb-2"><Form.Label className="small">Resend to (edit down to just the missed addresses)</Form.Label><Form.Control as="textarea" rows={2} value={resendValue} onChange={e => setResendValue(e.target.value)} /></Form.Group>
                            <Button size="sm" variant="success" className="me-2" onClick={sendResend} disabled={isLoading}>Send Resend</Button>
                            <Button size="sm" variant="outline-secondary" onClick={() => setResendOpenId(null)}>Cancel</Button>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            )}
          </Card.Body></Card>
        </Card.Body>
      </Card>

      {/* Fullscreen / Modal Email Preview */}
      <Modal 
        show={showPreviewModal} 
        onHide={() => setShowPreviewModal(false)} 
        size="lg" 
        centered
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton className="bg-dark text-white d-flex justify-content-between align-items-center">
          <Modal.Title className="h5 mb-0">
            ✉️ Email Client Preview
          </Modal.Title>
          <div className="d-flex gap-2 me-3">
            <Button 
              size="sm" 
              variant={previewDevice === 'desktop' ? 'light' : 'outline-light'}
              onClick={() => setPreviewDevice('desktop')}
            >
              💻 Desktop View
            </Button>
            <Button 
              size="sm" 
              variant={previewDevice === 'mobile' ? 'light' : 'outline-light'}
              onClick={() => setPreviewDevice('mobile')}
            >
              📱 Mobile View
            </Button>
          </div>
        </Modal.Header>
        <Modal.Body className="p-4" style={{ backgroundColor: '#f1f5f9', minHeight: '500px' }}>
          <div className="d-flex justify-content-center">
            <div style={{ 
              width: previewDevice === 'mobile' ? '380px' : '100%', 
              maxWidth: '740px',
              transition: 'all 0.3s ease',
              boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)',
              borderRadius: previewDevice === 'mobile' ? '28px' : '8px',
              border: previewDevice === 'mobile' ? '10px solid #1e293b' : 'none',
              overflow: 'hidden',
              background: '#ffffff'
            }}>
              {/* Fake Email Client Bar */}
              <div className="p-3 bg-light border-bottom small text-muted">
                <div className="mb-1">
                  <strong className="text-dark">Subject:</strong> {subject || '(No subject entered)'}
                </div>
                <div className="d-flex justify-content-between">
                  <span><strong className="text-dark">From:</strong> Survivor Fantasy Pool &lt;commissioner@survivorrealityfantasypool.com&gt;</span>
                  <span><strong className="text-dark">Date:</strong> {new Date().toLocaleDateString()}</span>
                </div>
                <div>
                  <strong className="text-dark">To:</strong> Sandra Diaz-Twine &lt;sandra@example.com&gt;
                </div>
              </div>

              {/* Rendered HTML */}
              <div 
                className="p-3"
                dangerouslySetInnerHTML={{ __html: renderSampleHtml(body || '*(No email message body entered yet)*', assets) }} 
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Close Preview
          </Button>
          <div className="d-flex gap-2">
            <Button 
              variant="outline-primary" 
              onClick={() => {
                setShowPreviewModal(false);
                sendTestEmail();
              }}
              disabled={isLoading || !subject.trim() || !body.trim()}
            >
              Send Test Email
            </Button>
            <Button 
              variant="success" 
              onClick={() => {
                setShowPreviewModal(false);
                sendCampaign();
              }}
              disabled={isLoading || !subject.trim() || !body.trim()}
            >
              Proceed to Send
            </Button>
          </div>
        </Modal.Footer>
      </Modal>

      {/* Image Search & Asset Picker Modal */}
      <Modal
        show={showImageSearchModal}
        onHide={() => setShowImageSearchModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>🖼️ Select Survivor Season {season} Images</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {/* Search bar & Refresh */}
          <Row className="mb-3 g-2 align-items-center">
            <Col md={8}>
              <Form.Control 
                type="text" 
                placeholder="Search images (e.g. logo, cast, tribal, aaliyah)..."
                value={imageSearchQuery}
                onChange={e => {
                  setImageSearchQuery(e.target.value);
                  loadSeasonAssets(season, e.target.value);
                }}
              />
            </Col>
            <Col md={4} className="d-flex gap-2">
              <Button 
                variant="outline-secondary" 
                className="w-100"
                onClick={() => loadSeasonAssets(season, imageSearchQuery)}
                disabled={isLoadingAssets}
              >
                {isLoadingAssets ? <Spinner size="sm" /> : '🔄 Refresh Season Images'}
              </Button>
            </Col>
          </Row>

          {/* Custom Image URL Accordion/Input */}
          <Card className="mb-4 bg-light border-0 shadow-sm">
            <Card.Body className="p-3">
              <h6 className="fw-bold mb-2">Or add custom image URL</h6>
              <Row className="g-2">
                <Col md={6}>
                  <Form.Control 
                    size="sm" 
                    placeholder="https://example.com/survivor-image.jpg" 
                    value={customImageUrl} 
                    onChange={e => setCustomImageUrl(e.target.value)} 
                  />
                </Col>
                <Col md={4}>
                  <Form.Control 
                    size="sm" 
                    placeholder="Image title (optional)" 
                    value={customImageTitle} 
                    onChange={e => setCustomImageTitle(e.target.value)} 
                  />
                </Col>
                <Col md={2}>
                  <Button 
                    size="sm" 
                    variant="success" 
                    className="w-100" 
                    onClick={handleAddCustomImage}
                    disabled={!customImageUrl.trim()}
                  >
                    ➕ Add Image
                  </Button>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Grid of Available Season Assets */}
          {isLoadingAssets ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <div className="mt-2 text-muted">Loading official season images...</div>
            </div>
          ) : availableSeasonAssets.length === 0 ? (
            <Alert variant="info" className="text-center">
              No matching images found for Season {season}. Try a different search keyword or paste a custom URL above.
            </Alert>
          ) : (
            <Row>
              {availableSeasonAssets.map((asset, i) => {
                const isSelected = assets.some(a => a.url === asset.url);
                return (
                  <Col md={4} lg={3} sm={6} key={`${asset.url}-${i}`} className="mb-3">
                    <Card className={`h-100 shadow-sm ${isSelected ? 'border-primary border-2' : ''}`}>
                      <div style={{ height: '140px', backgroundColor: '#1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                        <img 
                          src={getPreviewImageUrl(asset.url)} 
                          alt={asset.alt} 
                          referrerPolicy="no-referrer"
                          style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }}
                          loading="lazy"
                        />
                      </div>
                      <Card.Body className="p-2 d-flex flex-column justify-content-between">
                        <div className="small fw-bold text-truncate mb-2" title={asset.title}>
                          {asset.title}
                        </div>
                        {isSelected ? (
                          <Button 
                            size="sm" 
                            variant="outline-danger" 
                            className="w-100"
                            onClick={() => setAssets(curr => curr.filter(a => a.url !== asset.url))}
                          >
                            ✓ Added (Click to remove)
                          </Button>
                        ) : (
                          <Button 
                            size="sm" 
                            variant="primary" 
                            className="w-100"
                            onClick={() => addAsset(asset)}
                          >
                            ➕ Add to Email
                          </Button>
                        )}
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={() => setShowImageSearchModal(false)}>
            Done ({assets.length} images selected)
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Save Draft Modal */}
      <Modal
        show={showSaveDraftModal}
        onHide={() => setShowSaveDraftModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>💾 Save Email Draft</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="fw-bold">Draft Name / Template Title</Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. S51 Premiere - Main Draft, Rules Template"
              value={draftTitleInput}
              onChange={e => setDraftTitleInput(e.target.value)}
              autoFocus
            />
            <Form.Text className="text-muted">
              Give your draft a recognizable name to easily find and reuse it later.
            </Form.Text>
          </Form.Group>

          <div className="bg-light p-2 rounded border small text-muted">
            <div><strong>Season:</strong> {season} &bull; <strong>Type:</strong> {type} {type === 'weekly' ? `(Ep ${episode})` : ''}</div>
            <div><strong>Subject:</strong> {subject || '(No subject)'}</div>
            <div><strong>Assets:</strong> {assets.length} visual asset(s) attached</div>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between">
          <Button variant="secondary" onClick={() => setShowSaveDraftModal(false)}>
            Cancel
          </Button>
          <div className="d-flex gap-2">
            {activeDraftId && (
              <Button 
                variant="outline-primary" 
                onClick={() => handleConfirmSaveDraft(true)}
                disabled={isSavingDraft || (!subject.trim() && !body.trim())}
              >
                Save as New Copy
              </Button>
            )}
            <Button 
              variant="success" 
              onClick={() => handleConfirmSaveDraft(false)}
              disabled={isSavingDraft || (!subject.trim() && !body.trim())}
            >
              {isSavingDraft ? <Spinner size="sm" /> : (activeDraftId ? '💾 Overwrite / Save' : '💾 Save Draft')}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminEmail;
